import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";

import { areCookiesAccepted } from "@shared/tracking";

type CookieConsentContextType = {
  cookiesAccepted: boolean;
  dispatchCookieConsentChange: () => void;
};

const CookieConsentContext = createContext<CookieConsentContextType | null>(
  null,
);

type CookieConsentProviderProps = {
  children: ReactNode;
};

/**
 * Provider that manages cookie consent state centrally.
 * All components using useCookieConsent will share the same state and listeners.
 */
export default function CookieConsentProvider({
  children,
}: CookieConsentProviderProps) {
  const [cookiesAccepted, setCookiesAccepted] = useState(() =>
    typeof window !== "undefined" ? areCookiesAccepted() : false,
  );

  // Function to check current consent value and update state if necessary
  const dispatchCookieConsentChange = useCallback(() => {
    const currentValue = areCookiesAccepted();
    setCookiesAccepted((prev) => {
      // Only update if value actually changed to avoid unnecessary re-renders
      if (prev !== currentValue) {
        return currentValue;
      }
      return prev;
    });
  }, []);

  useEffect(() => {
    // Check periodically in case cookies are changed externally
    // (e.g., by browser extensions or other scripts)
    const interval = setInterval(() => {
      dispatchCookieConsentChange();
    }, 5000); // Check every 5 seconds

    return () => {
      clearInterval(interval);
    };
  }, [dispatchCookieConsentChange]);

  return (
    <CookieConsentContext.Provider
      value={{ cookiesAccepted, dispatchCookieConsentChange }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

/**
 * Hook that provides reactive cookie consent state.
 * Returns true if all cookies are accepted, false otherwise.
 * Updates automatically when cookie consent changes.
 *
 * @throws {Error} If used outside of CookieConsentProvider
 */
export function useCookieConsent(): boolean {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error(
      "useCookieConsent must be used within a CookieConsentProvider",
    );
  }
  return context.cookiesAccepted;
}

/**
 * Hook that provides the dispatch function to update cookie consent state.
 * Call this function whenever the cookie consent cookie is updated.
 *
 * @throws {Error} If used outside of CookieConsentProvider
 */
export function useDispatchCookieConsentChange(): () => void {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error(
      "useDispatchCookieConsentChange must be used within a CookieConsentProvider",
    );
  }
  return context.dispatchCookieConsentChange;
}
